const YEAR_MIN = 2005;
const YEAR_MAX = 2023;

export function experimentYears(): number[] {
  return Array.from({ length: YEAR_MAX - YEAR_MIN + 1 }, (_, i) => YEAR_MIN + i);
}

export function lerp(y: number, y0: number, v0: number, y1: number, v1: number): number {
  if (y1 === y0) return v0;
  const t = (y - y0) / (y1 - y0);
  return v0 + t * (v1 - v0);
}

/**
 * Rev2 electricity / gas residential trend fill:
 * - y <= 2015: scale physical shape to 2015 anchor
 * - 2016-2018: value lerp 2015-2019 anchors
 * - 2019: anchor
 * - 2020: lerp 2019 anchor → 2021 observation
 * - 2021-2023: observation knots
 */
export function buildPiecewiseResidentialOrSectorSeries(
  physical: Map<number, number>,
  knots: Map<number, number>
): Map<number, number> {
  const a2015 = knots.get(2015);
  const a2019 = knots.get(2019);
  const o2021 = knots.get(2021);
  const p2015 = physical.get(2015);

  if (a2015 == null || a2019 == null || p2015 == null || p2015 <= 0) {
    throw new Error('Missing 2015 anchor or physical baseline for piecewise series.');
  }
  if (o2021 == null) {
    throw new Error('Missing 2021 utility observation knot for piecewise series.');
  }

  const out = new Map<number, number>();
  for (const y of experimentYears()) {
    if (knots.has(y) && (y === 2015 || y === 2019 || y >= 2021)) {
      out.set(y, knots.get(y)!);
      continue;
    }
    if (y <= 2015) {
      const p = physical.get(y);
      out.set(y, p != null && p > 0 ? a2015 * (p / p2015) : a2015);
      continue;
    }
    if (y < 2019) {
      out.set(y, lerp(y, 2015, a2015, 2019, a2019));
      continue;
    }
    if (y === 2020) {
      out.set(y, lerp(y, 2019, a2019, 2021, o2021));
      continue;
    }
    if (y >= 2021 && knots.has(y)) {
      out.set(y, knots.get(y)!);
    }
  }
  return out;
}

export function buildPiecewiseNonresElecSeries(
  physicalCom: Map<number, number>,
  knots: Map<number, number>
): Map<number, number> {
  return buildPiecewiseResidentialOrSectorSeries(physicalCom, knots);
}
